import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  institutionUsers,
  institutionConfig,
  institutionDocuments,
  institutionWorkflows,
  type User,
  type DocumentType,
  type SecurityClassification,
  type DocumentDetail,
  type DocumentVersion,
  type WorkflowTemplate,
  type WorkflowInstance,
} from '@/services/institution';
import { api } from '@/services/api';
import { anomalyDetectionService, type AnomalyReport } from '@/services/ai';
import {
  ocrWorkerService,
  isOCRSupported,
  type OCRWorkerResult,
  type OCRProgress,
} from '@/services/ocrWorkerService';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import styles from './RecordsPage.module.css';

interface StaffRecord {
  id: string;
  title: string;
  description: string | null;
  referenceNumber: string | null;
  currentVersion: number;
  createdAt: string;
  updatedAt: string;
  documentType: {
    id: string;
    name: string;
    code: string;
  };
  securityClassification: {
    id: string;
    name: string;
    color: string;
    level: number;
  };
  status: {
    id: string;
    name: string;
    color: string;
    code?: string;
  };
  subjectUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string | null;
  } | null;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

// PFile (Personnel File) - aggregated view of a staff member's records
interface PFile {
  staffMember: User;
  records: StaffRecord[];
  totalRecords: number;
  categories: {
    employment: number;
    performance: number;
    training: number;
    leave: number;
    personnel: number;
  };
}

interface CreateRecordInput {
  title: string;
  description?: string;
  documentTypeId: string;
  securityClassificationId: string;
  subjectUserId?: string;
  referenceNumber?: string;
}

// Record type categories for filtering
const RECORD_CATEGORIES = [
  { value: '', label: 'All Record Types' },
  { value: 'employment', label: 'Employment Records', codes: ['APPOINTMENT_LETTER', 'CONFIRMATION_LETTER', 'PROMOTION_LETTER', 'POSTING_LETTER', 'SECONDMENT', 'RETIREMENT_NOTICE'] },
  { value: 'performance', label: 'Performance Records', codes: ['APPRAISAL', 'COMMENDATION', 'QUERY_LETTER', 'DISCIPLINARY'] },
  { value: 'training', label: 'Training & Development', codes: ['TRAINING_RECORD', 'STUDY_LEAVE', 'CERTIFICATE'] },
  { value: 'leave', label: 'Leave Records', codes: ['LEAVE_REQUEST', 'ASSUMPTION_DUTY'] },
  { value: 'personnel', label: 'Personnel Files', codes: ['PERSONNEL_FILE'] },
];

export function RecordsPage() {
  const [records, setRecords] = useState<StaffRecord[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [recordTypes, setRecordTypes] = useState<DocumentType[]>([]);
  const [classifications, setClassifications] = useState<SecurityClassification[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, _setFilterType] = useState(''); // TODO: Add document type filter UI

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateRecordInput>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PFile (Personnel File) sidebar
  const [selectedPFile, setSelectedPFile] = useState<PFile | null>(null);
  const [showPFileSidebar, setShowPFileSidebar] = useState(false);

  // Document detail modal
  const [selectedRecord, setSelectedRecord] = useState<DocumentDetail | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTab, setDetailTab] = useState<'details' | 'versions' | 'activity'>('details');
  const [loadingDetail, setLoadingDetail] = useState(false);

  // File upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadRecordId, setUploadRecordId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadChangeNotes, setUploadChangeNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // OCR state
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState<OCRProgress | null>(null);
  const [ocrResult, setOcrResult] = useState<OCRWorkerResult | null>(null);
  const [showOcrTextPreview, setShowOcrTextPreview] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState<DocumentDetail | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<CreateRecordInput>>({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Workflow modal
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflowRecordId, setWorkflowRecordId] = useState<string | null>(null);
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [startingWorkflow, setStartingWorkflow] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [recordWorkflows, setRecordWorkflows] = useState<WorkflowInstance[]>([]);

  // AI & Compliance Integration
  const [anomalyReports, setAnomalyReports] = useState<Map<string, AnomalyReport>>(new Map());
  const [showAnomalyPanel, setShowAnomalyPanel] = useState(false);
  const [selectedAnomalyReport, setSelectedAnomalyReport] = useState<AnomalyReport | null>(null);

  // Fetch records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterStaff) params.subjectUserId = filterStaff;
      if (filterType) params.documentTypeId = filterType;

      const response = await api.get<StaffRecord[]>('/v1/documents', params);
      if (response.success && response.data) {
        // Transform snake_case to camelCase
        const transformed = (response.data as unknown[]).map((r: unknown) => {
          const record = r as Record<string, unknown>;
          return {
            id: record.id,
            title: record.title,
            description: record.description,
            referenceNumber: record.reference_number ?? record.referenceNumber,
            currentVersion: record.current_version ?? record.currentVersion,
            createdAt: record.created_at ?? record.createdAt,
            updatedAt: record.updated_at ?? record.updatedAt,
            documentType: record.document_type ?? record.documentType,
            securityClassification: record.security_classification ?? record.securityClassification,
            status: record.status,
            subjectUser: record.subject_user ?? record.subjectUser,
            createdBy: record.created_by ?? record.createdBy,
          } as StaffRecord;
        });
        setRecords(transformed);
      }
    } catch (err) {
      console.error('Failed to fetch records:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStaff, filterType]);

  // Fetch reference data
  const fetchReferenceData = useCallback(async () => {
    try {
      const [usersRes, typesRes, classRes] = await Promise.all([
        institutionUsers.list({ perPage: 100 }),
        institutionConfig.getDocumentTypes(),
        institutionConfig.getSecurityClassifications(),
      ]);

      if (usersRes.success && usersRes.data) {
        setStaffMembers(usersRes.data);
      }
      if (typesRes.success && typesRes.data) {
        // Filter to personnel/staff record types
        const staffRecordCodes = RECORD_CATEGORIES.flatMap(c => c.codes || []);
        const staffTypes = typesRes.data.filter(t =>
          staffRecordCodes.includes(t.code) ||
          t.code.includes('PERSONNEL') ||
          t.code.includes('APPRAISAL') ||
          t.code.includes('LEAVE') ||
          t.code.includes('TRAINING')
        );
        setRecordTypes(staffTypes.length > 0 ? staffTypes : typesRes.data);
      }
      if (classRes.success && classRes.data) {
        setClassifications(classRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Run anomaly detection on records when they change
  useEffect(() => {
    if (records.length > 0) {
      const reports = new Map<string, AnomalyReport>();

      // Group records by staff member to analyze their complete file
      const staffRecords = new Map<string, StaffRecord[]>();
      records.forEach(record => {
        if (record.subjectUser) {
          const staffId = record.subjectUser.id;
          if (!staffRecords.has(staffId)) {
            staffRecords.set(staffId, []);
          }
          staffRecords.get(staffId)!.push(record);
        }
      });

      // Analyze each staff member's records
      staffRecords.forEach((staffRecs, staffId) => {
        const firstRecord = staffRecs[0];
        if (!firstRecord.subjectUser) return;

        const recordData = {
          id: staffId,
          staffName: `${firstRecord.subjectUser.firstName} ${firstRecord.subjectUser.lastName}`,
          fileNumber: firstRecord.subjectUser.employeeId || staffId,
          staffId: firstRecord.subjectUser.employeeId || undefined,
          status: 'active',
          isCheckedOut: false,
          documents: staffRecs.map(r => ({
            id: r.id,
            type: r.documentType?.code || 'UNKNOWN',
            name: r.title,
            uploadedAt: r.createdAt,
            isVerified: true,
          })),
          jackets: [],
          createdAt: new Date(Math.min(...staffRecs.map(r => new Date(r.createdAt).getTime()))).toISOString(),
          updatedAt: new Date(Math.max(...staffRecs.map(r => new Date(r.updatedAt).getTime()))).toISOString(),
        };

        const report = anomalyDetectionService.analyzePFile(recordData);
        if (report.anomalies.length > 0 || report.completenessScore < 100) {
          reports.set(staffId, report);
        }
      });

      setAnomalyReports(reports);
    }
  }, [records]);

  // Get flagged staff (with anomalies or low completeness)
  const flaggedStaff = useMemo(() => {
    const flagged: Array<{ staffId: string; staffName: string; report: AnomalyReport }> = [];
    anomalyReports.forEach((report, staffId) => {
      if (report.riskLevel === 'critical' || report.riskLevel === 'high') {
        flagged.push({ staffId, staffName: report.staffName, report });
      }
    });
    return flagged;
  }, [anomalyReports]);

  // Filter records by search query and category
  const filteredRecords = records.filter((record) => {
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch =
        record.title.toLowerCase().includes(search) ||
        record.referenceNumber?.toLowerCase().includes(search) ||
        record.subjectUser?.firstName.toLowerCase().includes(search) ||
        record.subjectUser?.lastName.toLowerCase().includes(search) ||
        record.subjectUser?.employeeId?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Category filter
    if (filterCategory) {
      const category = RECORD_CATEGORIES.find(c => c.value === filterCategory);
      if (category?.codes && !category.codes.includes(record.documentType.code)) {
        return false;
      }
    }

    return true;
  });

  function openCreateModal() {
    setFormData({
      title: '',
      description: '',
      documentTypeId: '',
      securityClassificationId: classifications.find(c => c.code === 'RESTRICTED')?.id || '',
      subjectUserId: '',
      referenceNumber: '',
    });
    setError(null);
    setShowCreateModal(true);
  }

  async function handleCreateRecord(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.title || !formData.documentTypeId || !formData.securityClassificationId) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const input = {
        title: formData.title,
        description: formData.description || undefined,
        documentTypeId: formData.documentTypeId,
        securityClassificationId: formData.securityClassificationId,
        subjectUserId: formData.subjectUserId || undefined,
        referenceNumber: formData.referenceNumber || undefined,
      };

      const response = await api.post('/v1/documents', input);
      if (response.success) {
        setShowCreateModal(false);
        fetchRecords();
      } else {
        setError(response.error?.message || 'Failed to create record');
      }
    } catch (err) {
      setError('An error occurred while creating the record');
    } finally {
      setSubmitting(false);
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function getStaffName(user: StaffRecord['subjectUser']): string {
    if (!user) return 'N/A';
    return `${user.firstName} ${user.lastName}`;
  }

  // Open PFile sidebar for a staff member
  function openPFile(staffId: string) {
    const staff = staffMembers.find(s => s.id === staffId);
    if (!staff) return;

    // Get all records for this staff member
    const staffRecords = records.filter(r => r.subjectUser?.id === staffId);

    // Calculate category counts
    const categories = {
      employment: 0,
      performance: 0,
      training: 0,
      leave: 0,
      personnel: 0,
    };

    staffRecords.forEach(record => {
      const code = record.documentType?.code || '';
      if (['APPOINTMENT_LETTER', 'CONFIRMATION_LETTER', 'PROMOTION_LETTER', 'POSTING_LETTER', 'SECONDMENT', 'RETIREMENT_NOTICE'].includes(code)) {
        categories.employment++;
      } else if (['APPRAISAL', 'COMMENDATION', 'QUERY_LETTER', 'DISCIPLINARY'].includes(code)) {
        categories.performance++;
      } else if (['TRAINING_RECORD', 'STUDY_LEAVE', 'CERTIFICATE'].includes(code)) {
        categories.training++;
      } else if (['LEAVE_REQUEST', 'ASSUMPTION_DUTY'].includes(code)) {
        categories.leave++;
      } else {
        categories.personnel++;
      }
    });

    setSelectedPFile({
      staffMember: staff,
      records: staffRecords,
      totalRecords: staffRecords.length,
      categories,
    });
    setShowPFileSidebar(true);
  }

  function closePFile() {
    setShowPFileSidebar(false);
    setSelectedPFile(null);
  }

  // Calculate unique staff members with PFiles
  const uniqueStaffWithRecords = new Set(records.map(r => r.subjectUser?.id).filter(Boolean));
  const pFileCount = uniqueStaffWithRecords.size;

  // Open document detail modal
  async function openDetailModal(recordId: string) {
    setShowDetailModal(true);
    setLoadingDetail(true);
    setDetailTab('details');

    try {
      // Fetch document and versions in parallel
      const [docResponse, versionsResponse] = await Promise.all([
        institutionDocuments.getById(recordId),
        institutionDocuments.getVersions(recordId),
      ]);

      if (docResponse.success && docResponse.data) {
        const record = docResponse.data;
        // Attach versions to the record
        if (versionsResponse.success && versionsResponse.data) {
          record.versions = versionsResponse.data;
        }
        setSelectedRecord(record);
      }
    } catch (err) {
      console.error('Failed to load record details:', err);
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeDetailModal() {
    setShowDetailModal(false);
    setSelectedRecord(null);
  }

  // Open upload modal
  function openUploadModal(recordId: string) {
    setUploadRecordId(recordId);
    setUploadFile(null);
    setUploadChangeNotes('');
    setUploadError(null);
    setOcrEnabled(false);
    setOcrProcessing(false);
    setOcrProgress(null);
    setOcrResult(null);
    setShowOcrTextPreview(false);
    setShowUploadModal(true);
  }

  function closeUploadModal() {
    setShowUploadModal(false);
    setUploadRecordId(null);
    setUploadFile(null);
    setUploadChangeNotes('');
    setUploadError(null);
    setOcrEnabled(false);
    setOcrProcessing(false);
    setOcrProgress(null);
    setOcrResult(null);
    setShowOcrTextPreview(false);
  }

  // Handle file selection with optional OCR
  async function handleFileSelect(file: File | null) {
    setUploadFile(file);
    setOcrResult(null);
    setOcrProgress(null);

    // If OCR is enabled and file is supported, process it
    if (file && ocrEnabled && isOCRSupported(file)) {
      await processFileWithOCR(file);
    }
  }

  // Process file with OCR
  async function processFileWithOCR(file: File) {
    if (!isOCRSupported(file)) {
      setUploadError('This file type is not supported for OCR. Supported types: PNG, JPG, JPEG, GIF, BMP, WEBP, TIFF');
      return;
    }

    setOcrProcessing(true);
    setOcrProgress({ progress: 0, status: 'Initializing OCR...' });
    setUploadError(null);

    try {
      const result = await ocrWorkerService.processImage(file, (progress) => {
        setOcrProgress(progress);
      });

      setOcrResult(result);
      setOcrProgress({ progress: 100, status: 'OCR complete' });

      // Auto-populate change notes with extracted info if empty
      if (!uploadChangeNotes && result.extractedData) {
        const autoNotes = generateAutoNotes(result);
        if (autoNotes) {
          setUploadChangeNotes(autoNotes);
        }
      }
    } catch (err) {
      console.error('OCR processing failed:', err);
      setUploadError(`OCR failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setOcrProcessing(false);
    }
  }

  // Generate automatic notes from OCR result
  function generateAutoNotes(result: OCRWorkerResult): string {
    const parts: string[] = [];

    if (result.extractedData.documentType && result.extractedData.documentType !== 'unknown') {
      parts.push(`Document type: ${result.extractedData.documentType.replace(/_/g, ' ')}`);
    }

    if (result.extractedData.staffName) {
      parts.push(`Staff: ${result.extractedData.staffName}`);
    }

    if (result.extractedData.staffId) {
      parts.push(`Staff ID: ${result.extractedData.staffId}`);
    }

    if (result.extractedData.dates.length > 0) {
      parts.push(`Date(s): ${result.extractedData.dates.slice(0, 3).join(', ')}`);
    }

    if (result.confidence > 0) {
      parts.push(`OCR confidence: ${result.confidence.toFixed(1)}%`);
    }

    return parts.join(' | ');
  }

  // Toggle OCR and process if file already selected
  async function handleOcrToggle() {
    const newEnabled = !ocrEnabled;
    setOcrEnabled(newEnabled);

    if (newEnabled && uploadFile && isOCRSupported(uploadFile)) {
      await processFileWithOCR(uploadFile);
    } else if (!newEnabled) {
      setOcrResult(null);
      setOcrProgress(null);
    }
  }

  // Get confidence class for styling
  function getConfidenceClass(confidence: number): string {
    if (confidence >= 80) return styles.high ?? '';
    if (confidence >= 50) return styles.medium ?? '';
    return styles.low ?? '';
  }

  async function handleUpload() {
    if (!uploadRecordId || !uploadFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Build change notes including OCR data if available
      let finalChangeNotes = uploadChangeNotes || undefined;

      // If OCR was performed, append extracted text metadata
      if (ocrResult) {
        const ocrMetadata = {
          ocrExtracted: true,
          ocrConfidence: ocrResult.confidence,
          extractedData: ocrResult.extractedData,
          extractedTextLength: ocrResult.text.length,
        };

        // Store OCR metadata in a structured way
        // The backend can parse this from the change notes
        const metadataNote = `[OCR_METADATA:${JSON.stringify(ocrMetadata)}]`;
        finalChangeNotes = finalChangeNotes
          ? `${finalChangeNotes}\n\n${metadataNote}`
          : metadataNote;
      }

      const response = await institutionDocuments.uploadFile(
        uploadRecordId,
        uploadFile,
        finalChangeNotes
      );

      if (response.success) {
        closeUploadModal();
        fetchRecords();
        // If detail modal is open, refresh it
        if (selectedRecord && selectedRecord.id === uploadRecordId) {
          openDetailModal(uploadRecordId);
        }
      } else {
        setUploadError(response.error?.message || 'Failed to upload file');
      }
    } catch (err) {
      setUploadError('An error occurred during upload');
    } finally {
      setUploading(false);
    }
  }

  // Open edit modal
  async function openEditModal(recordId: string) {
    setLoadingDetail(true);

    try {
      const response = await institutionDocuments.getById(recordId);
      if (response.success && response.data) {
        const record = response.data;
        setEditRecord(record);
        setEditFormData({
          title: record.title,
          description: record.description || '',
          documentTypeId: record.documentType.id,
          securityClassificationId: record.securityClassification.id,
          subjectUserId: record.subjectUser?.id || '',
          referenceNumber: record.referenceNumber || '',
        });
        setEditError(null);
        setShowEditModal(true);
      }
    } catch (err) {
      console.error('Failed to load record for editing:', err);
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeEditModal() {
    setShowEditModal(false);
    setEditRecord(null);
    setEditFormData({});
    setEditError(null);
  }

  async function handleEditRecord(e: React.FormEvent) {
    e.preventDefault();
    if (!editRecord) return;

    if (!editFormData.title || !editFormData.documentTypeId || !editFormData.securityClassificationId) {
      setEditError('Please fill in all required fields');
      return;
    }

    setEditSubmitting(true);
    setEditError(null);

    try {
      const input = {
        title: editFormData.title,
        description: editFormData.description || undefined,
        documentTypeId: editFormData.documentTypeId,
        securityClassificationId: editFormData.securityClassificationId,
        subjectUserId: editFormData.subjectUserId || null,
        referenceNumber: editFormData.referenceNumber || undefined,
      };

      const response = await institutionDocuments.update(editRecord.id, input);
      if (response.success) {
        closeEditModal();
        fetchRecords();
      } else {
        setEditError(response.error?.message || 'Failed to update record');
      }
    } catch (err) {
      setEditError('An error occurred while updating the record');
    } finally {
      setEditSubmitting(false);
    }
  }

  // Download file
  async function handleDownload(recordId: string, versionNumber?: number) {
    try {
      let blob: Blob | null;
      let fileName = `document-${recordId}`;

      if (versionNumber !== undefined) {
        blob = await institutionDocuments.downloadVersion(recordId, versionNumber);
        const version = selectedRecord?.versions?.find(v => v.versionNumber === versionNumber);
        if (version) {
          fileName = version.fileName;
        }
      } else {
        blob = await institutionDocuments.downloadCurrentVersion(recordId);
        // Get filename from current version
        if (selectedRecord?.versions && selectedRecord.versions.length > 0) {
          const currentVersion = selectedRecord.versions.find(v => v.versionNumber === selectedRecord.currentVersion);
          if (currentVersion) {
            fileName = currentVersion.fileName;
          }
        }
      }

      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function formatDateTime(dateStr: string): string {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Open workflow modal
  async function openWorkflowModal(recordId: string) {
    setWorkflowRecordId(recordId);
    setSelectedTemplateId('');
    setWorkflowError(null);
    setShowWorkflowModal(true);

    // Fetch templates and existing workflows for this record
    try {
      const [templatesRes, workflowsRes] = await Promise.all([
        institutionWorkflows.getTemplates(),
        institutionWorkflows.getWorkflowsForDocument(recordId),
      ]);

      if (templatesRes.success && templatesRes.data) {
        setWorkflowTemplates(templatesRes.data);
      }
      if (workflowsRes.success && workflowsRes.data) {
        setRecordWorkflows(workflowsRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch workflow data:', err);
    }
  }

  function closeWorkflowModal() {
    setShowWorkflowModal(false);
    setWorkflowRecordId(null);
    setSelectedTemplateId('');
    setWorkflowError(null);
    setRecordWorkflows([]);
  }

  async function handleStartWorkflow() {
    if (!workflowRecordId || !selectedTemplateId) {
      setWorkflowError('Please select a workflow template');
      return;
    }

    // Check if there's already an active workflow
    const activeWorkflow = recordWorkflows.find(
      (w) => w.status === 'pending' || w.status === 'in_progress'
    );
    if (activeWorkflow) {
      setWorkflowError('This record already has an active workflow');
      return;
    }

    setStartingWorkflow(true);
    setWorkflowError(null);

    try {
      const response = await institutionWorkflows.startWorkflow(selectedTemplateId, workflowRecordId);
      if (response.success) {
        closeWorkflowModal();
        fetchRecords();
      } else {
        setWorkflowError(response.error?.message || 'Failed to start workflow');
      }
    } catch (err) {
      setWorkflowError('An error occurred while starting the workflow');
    } finally {
      setStartingWorkflow(false);
    }
  }

  function getWorkflowStatusColor(status: string): string {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'rejected':
        return 'var(--color-error)';
      case 'cancelled':
        return 'var(--text-muted)';
      case 'in_progress':
      case 'pending':
      default:
        return 'var(--color-warning)';
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Staff Records</h1>
          <p className={styles.subtitle}>Manage personnel records and files</p>
        </div>
        <div className={styles.headerActions}>
          {flaggedStaff.length > 0 && (
            <Button variant="secondary" onClick={() => setShowAnomalyPanel(true)} className={styles.alertBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className={styles.alertBadge}>{flaggedStaff.length}</span>
              Records Need Attention
            </Button>
          )}
          <Button onClick={openCreateModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Record
          </Button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search by title, reference, or staff name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <select
          value={filterStaff}
          onChange={(e) => setFilterStaff(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Staff Members</option>
          {staffMembers.map((staff) => (
            <option key={staff.id} value={staff.id}>
              {staff.firstName} {staff.lastName} {staff.employeeId ? `(${staff.employeeId})` : ''}
            </option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className={styles.filterSelect}
        >
          {RECORD_CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.statsBar}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{records.length}</span>
          <span className={styles.statLabel}>Total Records</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{pFileCount}</span>
          <span className={styles.statLabel}>Active PFiles</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{records.filter(r => r.status?.code === 'DRAFT').length}</span>
          <span className={styles.statLabel}>Drafts</span>
        </div>
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading records...</div>
        ) : filteredRecords.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
              <path d="M14 2V8H20" />
            </svg>
            <p>No records found</p>
            <Button variant="secondary" onClick={openCreateModal}>Create First Record</Button>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Record</th>
                <th>Staff Member</th>
                <th>Type</th>
                <th>Classification</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={record.id}>
                  <td>
                    <div className={styles.recordCell}>
                      <div className={styles.recordIcon}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                          <path d="M14 2V8H20" />
                        </svg>
                      </div>
                      <div className={styles.recordInfo}>
                        <span className={styles.recordTitle}>{record.title}</span>
                        {record.referenceNumber && (
                          <span className={styles.recordRef}>{record.referenceNumber}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {record.subjectUser ? (
                      <button
                        className={styles.staffCell}
                        onClick={() => openPFile(record.subjectUser!.id)}
                        title={`View PFile for ${getStaffName(record.subjectUser)}`}
                      >
                        <div className={styles.staffAvatar}>
                          {record.subjectUser.firstName[0]}{record.subjectUser.lastName[0]}
                        </div>
                        <div className={styles.staffInfo}>
                          <span className={styles.staffName}>{getStaffName(record.subjectUser)}</span>
                          {record.subjectUser.employeeId && (
                            <span className={styles.staffId}>{record.subjectUser.employeeId}</span>
                          )}
                        </div>
                        <svg className={styles.pfileIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </button>
                    ) : (
                      <span className={styles.noStaff}>Not assigned</span>
                    )}
                  </td>
                  <td>
                    <span className={styles.typeBadge}>{record.documentType?.name}</span>
                  </td>
                  <td>
                    <span
                      className={styles.classificationBadge}
                      style={{ backgroundColor: `${record.securityClassification?.color}20`, color: record.securityClassification?.color }}
                    >
                      {record.securityClassification?.name}
                    </span>
                  </td>
                  <td>
                    <span
                      className={styles.statusBadge}
                      style={{ backgroundColor: `${record.status?.color}20`, color: record.status?.color }}
                    >
                      {record.status?.name}
                    </span>
                  </td>
                  <td>
                    <span className={styles.dateText}>{formatDate(record.createdAt)}</span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        title="View"
                        onClick={(e) => { e.stopPropagation(); openDetailModal(record.id); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        className={styles.actionBtn}
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); openEditModal(record.id); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className={styles.actionBtn}
                        title="Upload File"
                        onClick={(e) => { e.stopPropagation(); openUploadModal(record.id); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </button>
                      <button
                        className={styles.actionBtn}
                        title="Start Workflow"
                        onClick={(e) => { e.stopPropagation(); openWorkflowModal(record.id); }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4H8V8H4V4ZM12 4H16V8H12V4ZM12 12H16V16H12V12ZM8 6H12M14 8V12M8 14H12" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* PFile Sidebar */}
      {showPFileSidebar && selectedPFile && (
        <div className={styles.pfileSidebarOverlay} onClick={closePFile}>
          <div className={styles.pfileSidebar} onClick={(e) => e.stopPropagation()}>
            <div className={styles.pfileHeader}>
              <div className={styles.pfileHeaderContent}>
                <div className={styles.pfileAvatar}>
                  {selectedPFile.staffMember.firstName[0]}{selectedPFile.staffMember.lastName[0]}
                </div>
                <div className={styles.pfileHeaderInfo}>
                  <h2 className={styles.pfileTitle}>
                    {selectedPFile.staffMember.firstName} {selectedPFile.staffMember.lastName}
                  </h2>
                  <p className={styles.pfileSubtitle}>Personnel File (PFile)</p>
                  {selectedPFile.staffMember.employeeId && (
                    <span className={styles.pfileStaffId}>Staff ID: {selectedPFile.staffMember.employeeId}</span>
                  )}
                </div>
              </div>
              <button className={styles.pfileCloseBtn} onClick={closePFile}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={styles.pfileStats}>
              <div className={styles.pfileStat}>
                <span className={styles.pfileStatValue}>{selectedPFile.totalRecords}</span>
                <span className={styles.pfileStatLabel}>Total Records</span>
              </div>
              <div className={styles.pfileStat}>
                <span className={styles.pfileStatValue}>{selectedPFile.categories.employment}</span>
                <span className={styles.pfileStatLabel}>Employment</span>
              </div>
              <div className={styles.pfileStat}>
                <span className={styles.pfileStatValue}>{selectedPFile.categories.performance}</span>
                <span className={styles.pfileStatLabel}>Performance</span>
              </div>
              <div className={styles.pfileStat}>
                <span className={styles.pfileStatValue}>{selectedPFile.categories.training}</span>
                <span className={styles.pfileStatLabel}>Training</span>
              </div>
              <div className={styles.pfileStat}>
                <span className={styles.pfileStatValue}>{selectedPFile.categories.leave}</span>
                <span className={styles.pfileStatLabel}>Leave</span>
              </div>
            </div>

            <div className={styles.pfileStaffDetails}>
              <h3 className={styles.pfileSectionTitle}>Staff Details</h3>
              <div className={styles.pfileDetailGrid}>
                <div className={styles.pfileDetailItem}>
                  <span className={styles.pfileDetailLabel}>Email</span>
                  <span className={styles.pfileDetailValue}>{selectedPFile.staffMember.email}</span>
                </div>
                <div className={styles.pfileDetailItem}>
                  <span className={styles.pfileDetailLabel}>Department</span>
                  <span className={styles.pfileDetailValue}>{selectedPFile.staffMember.department?.name || 'Not assigned'}</span>
                </div>
                <div className={styles.pfileDetailItem}>
                  <span className={styles.pfileDetailLabel}>Status</span>
                  <span className={`${styles.pfileDetailValue} ${selectedPFile.staffMember.isActive ? styles.active : styles.inactive}`}>
                    {selectedPFile.staffMember.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.pfileRecords}>
              <h3 className={styles.pfileSectionTitle}>Records in PFile ({selectedPFile.records.length})</h3>
              {selectedPFile.records.length === 0 ? (
                <div className={styles.pfileEmptyRecords}>
                  <p>No records found for this staff member</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      closePFile();
                      setFormData((prev) => ({ ...prev, subjectUserId: selectedPFile.staffMember.id }));
                      openCreateModal();
                    }}
                  >
                    Add First Record
                  </Button>
                </div>
              ) : (
                <div className={styles.pfileRecordsList}>
                  {selectedPFile.records.map((record) => (
                    <div
                      key={record.id}
                      className={styles.pfileRecordItem}
                      onClick={() => { closePFile(); openDetailModal(record.id); }}
                    >
                      <div className={styles.pfileRecordIcon}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                          <path d="M14 2V8H20" />
                        </svg>
                      </div>
                      <div className={styles.pfileRecordInfo}>
                        <span className={styles.pfileRecordTitle}>{record.title}</span>
                        <span className={styles.pfileRecordMeta}>
                          {record.documentType?.name} • {formatDate(record.createdAt)}
                        </span>
                      </div>
                      <span
                        className={styles.pfileRecordStatus}
                        style={{ backgroundColor: `${record.status?.color}20`, color: record.status?.color }}
                      >
                        {record.status?.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.pfileActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  closePFile();
                  setFormData((prev) => ({ ...prev, subjectUserId: selectedPFile.staffMember.id }));
                  openCreateModal();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Record to PFile
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Record Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Staff Record"
      >
        <form onSubmit={handleCreateRecord}>
          {error && <div className={styles.error}>{error}</div>}

          <Input
            label="Record Title"
            value={formData.title || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            required
            hint="e.g., Performance Appraisal 2025, Training Certificate - Excel"
          />

          <Select
            label="Staff Member (Subject)"
            value={formData.subjectUserId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, subjectUserId: e.target.value }))}
          >
            <option value="">Select Staff Member</option>
            {staffMembers.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} {staff.employeeId ? `(${staff.employeeId})` : ''} - {staff.department?.name || 'No Dept'}
              </option>
            ))}
          </Select>

          <Select
            label="Record Type"
            value={formData.documentTypeId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, documentTypeId: e.target.value }))}
            required
          >
            <option value="">Select Record Type</option>
            {recordTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>

          <Select
            label="Security Classification"
            value={formData.securityClassificationId || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, securityClassificationId: e.target.value }))}
            required
          >
            <option value="">Select Classification</option>
            {classifications.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </Select>

          <Input
            label="Reference Number"
            value={formData.referenceNumber || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, referenceNumber: e.target.value }))}
            hint="Optional - e.g., HR/2025/001"
          />

          <Input
            label="Description"
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          />

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Record'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Document Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={closeDetailModal}
        title="Record Details"
      >
        {loadingDetail ? (
          <div className={styles.loading}>Loading record details...</div>
        ) : selectedRecord ? (
          <div className={styles.detailModal}>
            <div className={styles.detailTabs}>
              <button
                className={`${styles.detailTab} ${detailTab === 'details' ? styles.activeTab : ''}`}
                onClick={() => setDetailTab('details')}
              >
                Details
              </button>
              <button
                className={`${styles.detailTab} ${detailTab === 'versions' ? styles.activeTab : ''}`}
                onClick={() => setDetailTab('versions')}
              >
                Versions ({selectedRecord.versions?.length || 0})
              </button>
            </div>

            {detailTab === 'details' && (
              <div className={styles.detailContent}>
                <div className={styles.detailSection}>
                  <h4 className={styles.detailSectionTitle}>Record Information</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Title</span>
                      <span className={styles.detailValue}>{selectedRecord.title}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Reference Number</span>
                      <span className={styles.detailValue}>{selectedRecord.referenceNumber || 'N/A'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Record Type</span>
                      <span className={styles.detailValue}>{selectedRecord.documentType.name}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Classification</span>
                      <span
                        className={styles.classificationBadge}
                        style={{
                          backgroundColor: `${selectedRecord.securityClassification.color}20`,
                          color: selectedRecord.securityClassification.color
                        }}
                      >
                        {selectedRecord.securityClassification.name}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Status</span>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: `${selectedRecord.status.color}20`,
                          color: selectedRecord.status.color
                        }}
                      >
                        {selectedRecord.status.name}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Current Version</span>
                      <span className={styles.detailValue}>v{selectedRecord.currentVersion}</span>
                    </div>
                  </div>
                </div>

                {selectedRecord.description && (
                  <div className={styles.detailSection}>
                    <h4 className={styles.detailSectionTitle}>Description</h4>
                    <p className={styles.detailDescription}>{selectedRecord.description}</p>
                  </div>
                )}

                <div className={styles.detailSection}>
                  <h4 className={styles.detailSectionTitle}>Staff Member</h4>
                  {selectedRecord.subjectUser ? (
                    <div className={styles.staffDetailCard}>
                      <div className={styles.staffAvatar}>
                        {selectedRecord.subjectUser.firstName[0]}{selectedRecord.subjectUser.lastName[0]}
                      </div>
                      <div className={styles.staffDetailInfo}>
                        <span className={styles.staffDetailName}>
                          {selectedRecord.subjectUser.firstName} {selectedRecord.subjectUser.lastName}
                        </span>
                        <span className={styles.staffDetailEmail}>{selectedRecord.subjectUser.email}</span>
                        {selectedRecord.subjectUser.employeeId && (
                          <span className={styles.staffDetailId}>ID: {selectedRecord.subjectUser.employeeId}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className={styles.noData}>No staff member assigned</p>
                  )}
                </div>

                <div className={styles.detailSection}>
                  <h4 className={styles.detailSectionTitle}>Metadata</h4>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Created</span>
                      <span className={styles.detailValue}>{formatDateTime(selectedRecord.createdAt)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Created By</span>
                      <span className={styles.detailValue}>
                        {selectedRecord.createdBy.firstName} {selectedRecord.createdBy.lastName}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Last Updated</span>
                      <span className={styles.detailValue}>{formatDateTime(selectedRecord.updatedAt)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Record ID</span>
                      <span className={styles.detailValue} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {selectedRecord.id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailActions}>
                  {selectedRecord.versions && selectedRecord.versions.length > 0 && (
                    <Button onClick={() => handleDownload(selectedRecord.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download File
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => { closeDetailModal(); openUploadModal(selectedRecord.id); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload New Version
                  </Button>
                  <Button variant="secondary" onClick={() => { closeDetailModal(); openEditModal(selectedRecord.id); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit Record
                  </Button>
                  <Button variant="secondary" onClick={() => { closeDetailModal(); openWorkflowModal(selectedRecord.id); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4H8V8H4V4ZM12 4H16V8H12V4ZM12 12H16V16H12V12ZM8 6H12M14 8V12M8 14H12" />
                    </svg>
                    Start Workflow
                  </Button>
                </div>
              </div>
            )}

            {detailTab === 'versions' && (
              <div className={styles.versionsContent}>
                {!selectedRecord.versions || selectedRecord.versions.length === 0 ? (
                  <div className={styles.noVersions}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <p>No files uploaded yet</p>
                    <Button onClick={() => { closeDetailModal(); openUploadModal(selectedRecord.id); }}>
                      Upload First File
                    </Button>
                  </div>
                ) : (
                  <div className={styles.versionsList}>
                    {selectedRecord.versions.map((version: DocumentVersion) => {
                      const isCurrentVersion = version.versionNumber === selectedRecord.currentVersion;
                      return (
                        <div key={version.id} className={`${styles.versionItem} ${isCurrentVersion ? styles.currentVersion : ''}`}>
                          <div className={styles.versionIcon}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </div>
                          <div className={styles.versionInfo}>
                            <div className={styles.versionHeader}>
                              <span className={styles.versionNumber}>Version {version.versionNumber}</span>
                              {isCurrentVersion && (
                                <span className={styles.currentBadge}>Current</span>
                              )}
                            </div>
                            <span className={styles.versionFileName}>{version.fileName}</span>
                            <div className={styles.versionMeta}>
                              <span>{formatFileSize(version.fileSize)}</span>
                              <span>-</span>
                              <span>{formatDateTime(version.createdAt)}</span>
                            </div>
                            {version.changeNotes && (
                              <p className={styles.versionComments}>{version.changeNotes}</p>
                            )}
                          </div>
                          <button
                            className={styles.downloadVersionBtn}
                            onClick={() => handleDownload(selectedRecord.id, version.versionNumber)}
                            title="Download this version"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.error}>Failed to load record details</div>
        )}
      </Modal>

      {/* File Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={closeUploadModal}
        title="Upload File"
      >
        <div className={styles.uploadModal}>
          {uploadError && <div className={styles.error}>{uploadError}</div>}

          {/* OCR Toggle */}
          <div className={styles.ocrToggle}>
            <div className={styles.ocrToggleLabel}>
              <span className={styles.ocrToggleTitle}>Extract Text with OCR</span>
              <span className={styles.ocrToggleHint}>
                Automatically extract text from images using AI-powered OCR
              </span>
            </div>
            <button
              type="button"
              className={`${styles.ocrSwitch} ${ocrEnabled ? styles.active : ''}`}
              onClick={handleOcrToggle}
              disabled={ocrProcessing}
            >
              <span className={styles.ocrSwitchKnob} />
            </button>
          </div>

          <div className={styles.uploadDropzone} onClick={() => fileInputRef.current?.click()}>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
            />
            {uploadFile ? (
              <div className={styles.selectedFile}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <div className={styles.fileDetails}>
                  <span className={styles.fileName}>{uploadFile.name}</span>
                  <span className={styles.fileSize}>{formatFileSize(uploadFile.size)}</span>
                </div>
                <button
                  className={styles.removeFileBtn}
                  onClick={(e) => { e.stopPropagation(); handleFileSelect(null); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p>Click to select a file or drag and drop</p>
                <span className={styles.uploadHint}>PDF, DOC, DOCX, XLS, XLSX, JPG, PNG up to 25MB</span>
              </>
            )}
          </div>

          {/* OCR Progress Indicator */}
          {ocrProcessing && ocrProgress && (
            <div className={styles.ocrProgress}>
              <div className={styles.ocrProgressHeader}>
                <div className={styles.ocrProgressSpinner} />
                <span className={styles.ocrProgressStatus}>{ocrProgress.status}</span>
              </div>
              <div className={styles.ocrProgressBar}>
                <div
                  className={styles.ocrProgressFill}
                  style={{ width: `${ocrProgress.progress}%` }}
                />
              </div>
              <span className={styles.ocrProgressPercent}>{ocrProgress.progress}%</span>
            </div>
          )}

          {/* OCR Results Preview */}
          {ocrResult && !ocrProcessing && (
            <div className={styles.ocrResults}>
              <div className={styles.ocrResultsHeader}>
                <span className={styles.ocrResultsTitle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Text Extracted Successfully
                </span>
                <span className={`${styles.ocrConfidence} ${getConfidenceClass(ocrResult.confidence)}`}>
                  {ocrResult.confidence.toFixed(1)}% confidence
                </span>
              </div>

              {/* Extracted Data Grid */}
              <div className={styles.ocrExtractedData}>
                {ocrResult.extractedData.documentType && ocrResult.extractedData.documentType !== 'unknown' && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>Document Type</span>
                    <span className={styles.ocrDataValue}>
                      {ocrResult.extractedData.documentType.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
                {ocrResult.extractedData.staffName && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>Staff Name</span>
                    <span className={styles.ocrDataValue}>{ocrResult.extractedData.staffName}</span>
                  </div>
                )}
                {ocrResult.extractedData.staffId && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>Staff ID</span>
                    <span className={styles.ocrDataValue}>{ocrResult.extractedData.staffId}</span>
                  </div>
                )}
                {ocrResult.extractedData.grade && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>Grade</span>
                    <span className={styles.ocrDataValue}>{ocrResult.extractedData.grade}</span>
                  </div>
                )}
                {ocrResult.extractedData.dates.length > 0 && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>Dates Found</span>
                    <span className={styles.ocrDataValue}>
                      {ocrResult.extractedData.dates.slice(0, 3).join(', ')}
                      {ocrResult.extractedData.dates.length > 3 && ` +${ocrResult.extractedData.dates.length - 3} more`}
                    </span>
                  </div>
                )}
                {ocrResult.extractedData.fileNumbers.length > 0 && (
                  <div className={styles.ocrDataItem}>
                    <span className={styles.ocrDataLabel}>File Numbers</span>
                    <span className={styles.ocrDataValue}>
                      {ocrResult.extractedData.fileNumbers.join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Text Preview Toggle */}
              <button
                type="button"
                className={styles.ocrTogglePreview}
                onClick={() => setShowOcrTextPreview(!showOcrTextPreview)}
              >
                {showOcrTextPreview ? 'Hide extracted text' : 'Show extracted text'}
              </button>

              {showOcrTextPreview && (
                <div className={styles.ocrTextPreview}>
                  {ocrResult.text.slice(0, 500)}
                  {ocrResult.text.length > 500 && '...'}
                </div>
              )}
            </div>
          )}

          <div className={styles.uploadComments}>
            <label className={styles.uploadLabel}>Change Notes (optional)</label>
            <textarea
              className={styles.uploadTextarea}
              placeholder="Describe what changed in this version..."
              value={uploadChangeNotes}
              onChange={(e) => setUploadChangeNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeUploadModal}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading || ocrProcessing}>
              {uploading ? 'Uploading...' : ocrProcessing ? 'Processing OCR...' : 'Upload File'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Record Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={closeEditModal}
        title="Edit Staff Record"
      >
        <form onSubmit={handleEditRecord}>
          {editError && <div className={styles.error}>{editError}</div>}

          <Input
            label="Record Title"
            value={editFormData.title || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <Select
            label="Staff Member (Subject)"
            value={editFormData.subjectUserId || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, subjectUserId: e.target.value }))}
          >
            <option value="">Select Staff Member</option>
            {staffMembers.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.firstName} {staff.lastName} {staff.employeeId ? `(${staff.employeeId})` : ''} - {staff.department?.name || 'No Dept'}
              </option>
            ))}
          </Select>

          <Select
            label="Record Type"
            value={editFormData.documentTypeId || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, documentTypeId: e.target.value }))}
            required
          >
            <option value="">Select Record Type</option>
            {recordTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>

          <Select
            label="Security Classification"
            value={editFormData.securityClassificationId || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, securityClassificationId: e.target.value }))}
            required
          >
            <option value="">Select Classification</option>
            {classifications.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </Select>

          <Input
            label="Reference Number"
            value={editFormData.referenceNumber || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, referenceNumber: e.target.value }))}
            hint="Optional - e.g., HR/2025/001"
          />

          <Input
            label="Description"
            value={editFormData.description || ''}
            onChange={(e) => setEditFormData((prev) => ({ ...prev, description: e.target.value }))}
          />

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={editSubmitting}>
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Start Workflow Modal */}
      <Modal
        isOpen={showWorkflowModal}
        onClose={closeWorkflowModal}
        title="Start Approval Workflow"
      >
        <div className={styles.workflowModal}>
          {workflowError && <div className={styles.error}>{workflowError}</div>}

          {/* Active Workflow Warning */}
          {recordWorkflows.some((w) => w.status === 'pending' || w.status === 'in_progress') && (
            <div className={styles.workflowWarning}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>This record already has an active workflow in progress.</span>
            </div>
          )}

          {/* Workflow History */}
          {recordWorkflows.length > 0 && (
            <div className={styles.workflowHistory}>
              <h4 className={styles.workflowHistoryTitle}>Workflow History</h4>
              <div className={styles.workflowHistoryList}>
                {recordWorkflows.map((wf) => (
                  <div key={wf.id} className={styles.workflowHistoryItem}>
                    <span className={styles.workflowTemplateName}>{wf.template?.name || 'Workflow'}</span>
                    <span
                      className={styles.workflowStatusBadge}
                      style={{
                        backgroundColor: `${getWorkflowStatusColor(wf.status)}20`,
                        color: getWorkflowStatusColor(wf.status),
                      }}
                    >
                      {wf.status.replace('_', ' ')}
                    </span>
                    <span className={styles.workflowDate}>{formatDate(wf.startedAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Select Template */}
          <div className={styles.workflowSelect}>
            <Select
              label="Select Workflow Template"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={recordWorkflows.some((w) => w.status === 'pending' || w.status === 'in_progress')}
            >
              <option value="">Choose a template...</option>
              {workflowTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Select>
            {workflowTemplates.length === 0 && (
              <p className={styles.noTemplates}>
                No workflow templates available. Create one in Administration &gt; Workflows.
              </p>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button type="button" variant="secondary" onClick={closeWorkflowModal}>
              Cancel
            </Button>
            <Button
              onClick={handleStartWorkflow}
              disabled={
                startingWorkflow ||
                !selectedTemplateId ||
                recordWorkflows.some((w) => w.status === 'pending' || w.status === 'in_progress')
              }
            >
              {startingWorkflow ? 'Starting...' : 'Start Workflow'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Anomaly Report Modal */}
      <Modal
        isOpen={!!selectedAnomalyReport}
        onClose={() => setSelectedAnomalyReport(null)}
        title="Record Health Report"
      >
        {selectedAnomalyReport && (
          <div className={styles.anomalyModal}>
            <div className={styles.anomalyHeader}>
              <div className={styles.anomalyStaff}>{selectedAnomalyReport.staffName}</div>
              <div className={`${styles.riskBadge} ${styles[selectedAnomalyReport.riskLevel]}`}>
                {selectedAnomalyReport.riskLevel} Risk
              </div>
            </div>

            <div className={styles.completenessSection}>
              <div className={styles.completenessLabel}>Completeness Score</div>
              <div className={styles.completenessBar}>
                <div
                  className={styles.completenessProgress}
                  style={{
                    width: `${selectedAnomalyReport.completenessScore}%`,
                    backgroundColor: selectedAnomalyReport.completenessScore >= 80 ? 'var(--color-success)' :
                                     selectedAnomalyReport.completenessScore >= 50 ? 'var(--color-warning)' : 'var(--color-error)'
                  }}
                />
              </div>
              <div className={styles.completenessValue}>{selectedAnomalyReport.completenessScore}%</div>
            </div>

            {selectedAnomalyReport.anomalies.length > 0 && (
              <div className={styles.anomaliesSection}>
                <h4>Issues Detected ({selectedAnomalyReport.anomalies.length})</h4>
                <div className={styles.anomaliesList}>
                  {selectedAnomalyReport.anomalies.map((anomaly, index) => (
                    <div key={index} className={`${styles.anomalyItem} ${styles[anomaly.severity]}`}>
                      <div className={styles.anomalyType}>{anomaly.type.replace(/_/g, ' ')}</div>
                      <div className={styles.anomalyMessage}>{anomaly.message}</div>
                      {anomaly.suggestedAction && (
                        <div className={styles.anomalyRecommendation}>
                          <strong>Recommendation:</strong> {anomaly.suggestedAction}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedAnomalyReport.recommendations.length > 0 && (
              <div className={styles.recommendationsSection}>
                <h4>Recommendations</h4>
                <ul className={styles.recommendationsList}>
                  {selectedAnomalyReport.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.modalActions}>
              <Button variant="secondary" onClick={() => setSelectedAnomalyReport(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Anomaly Panel Modal - Shows all flagged records */}
      <Modal
        isOpen={showAnomalyPanel}
        onClose={() => setShowAnomalyPanel(false)}
        title="Records Needing Attention"
      >
        <div className={styles.anomalyPanel}>
          <p className={styles.anomalyPanelDescription}>
            The following staff records have been flagged by the AI system for potential issues.
          </p>
          <div className={styles.flaggedList}>
            {flaggedStaff.map(({ staffId, staffName, report }) => (
              <div
                key={staffId}
                className={`${styles.flaggedItem} ${styles[report.riskLevel]}`}
                onClick={() => {
                  setSelectedAnomalyReport(report);
                  setShowAnomalyPanel(false);
                }}
              >
                <div className={styles.flaggedHeader}>
                  <span className={styles.flaggedFileNumber}>{staffName}</span>
                  <span className={`${styles.riskBadge} ${styles[report.riskLevel]}`}>
                    {report.riskLevel}
                  </span>
                </div>
                <div className={styles.flaggedStats}>
                  <span>{report.anomalies.length} issues</span>
                  <span>{report.completenessScore}% complete</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
